import * as React from 'react';
import useTranslate from '../../../../../common/hooks/useTranslate';
import { useState, useMemo, useRef, useEffect } from 'react';
import { formatNumber } from '@progress/kendo-intl';
import { exportImage } from '@progress/kendo-drawing';
import { saveAs } from '@progress/kendo-file-saver';
import { exportPDF } from '@progress/kendo-drawing';
import { exportSVG } from '@progress/kendo-drawing';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import 'hammerjs';
import { useDispatch } from 'react-redux';
import useGetChartData from './useGetChartData';
import useClickOutside from '../../../../../common/hooks/useClickOutside';
import { SWITCH_CASH_FLOW_MODAL } from '../../../../../redux/types';

import { IWidget } from '../../../types/IWidget';

import {
  Chart,
  ChartSeries,
  ChartSeriesItem,
  ChartCategoryAxis,
  ChartCategoryAxisItem,
  ChartLegend,
  ChartValueAxis,
  ChartValueAxisItem,
  ChartSeriesItemTooltip,
  ChartTooltip,
  ChartSeriesLabels,
  exportVisual,
} from '@progress/kendo-react-charts';
import { Grid, GridColumn, GridSortChangeEvent } from '@progress/kendo-react-grid';
import { ExcelExport, ExcelExportColumn, ExcelExportColumnGroup } from '@progress/kendo-react-excel-export';
import { Offset, Popup } from '@progress/kendo-react-popup';
import WidgetTitle from '../widgetTitle/widgetTitle';
import GridCell from './gridCell';
import Icon from '../../../../ui/icon/icon';
import OutletLoader from '../../../../ui/loaders/outletLoader/outletLoader';

import BrandingStore from '../../../../../common/services/config/brandingStore';

import './charts.scss';
import { Rect } from '@progress/kendo-drawing/geometry';

const initialSort: Array<SortDescriptor> = [{ field: 'periodName', dir: 'asc' }];

const ChartWidget: React.FC<IWidget> = ({
                                          setup,
                                          widget,
                                          measures,
                                          scenarios,
                                          periods,
                                          onCollapse,
                                          onExpand,
                                          expand,
                                          notes,
                                          boardId,
                                          id,
                                          handleWidgetChange,
                                          widgets,
                                          changeOldIndex,
                                          handleWidgetMove,
                                          changeNewIndex,
                                          customisation,
                                          selectedObject,
                                          boardSelectedCurrency,
                                          selectedPeriod,
                                          investorObjectId,
                                          homePage,
                                          handleUnpin,
                                          handlePinWidget,
                                          handleUnpinWidget,
                                          unpinId,
                                        }) => {
  const [position, setPosition] = useState<Offset>({ left: 0, top: 0 });
  const [gridView, setGridView] = useState<boolean>(false);
  const [sort, setSort] = useState<SortDescriptor[]>(initialSort);
  const [isMobile, setIsMobile] = useState<boolean>(false);

  const mm = (val) => val * 2.8347;
  const PAGE_RECT = new Rect([0, 0], [mm(297), mm(210)]);

  const dispatch = useDispatch();
  const { t } = useTranslate();

  const chart = useRef(null);
  const popupWrapperRef = useRef(null);
  const exportRef = useRef(null);
  const grid = useRef(null);

  const {
    loading,
    chartData,
    series,
    categories,
    valueAxis,
    seriesClick,
    cashFlow,
    drillDownData,
    drillDown,
    popupVisible,
    setPopUpVisible,
    breadCrumbs,
    widgetData,
    gridData,
    gridOptions,
    chartNotes,
    drillToLevel,
    gridCellClick,
    currencies,
    selectedCurrency,
    handleCurrencyChange,
  } = useGetChartData(setup, periods, measures, widget, notes, scenarios, selectedObject, selectedPeriod, boardSelectedCurrency, investorObjectId);

  const onImageExportClick = () => {
    const chartVisual = exportVisual(chart.current);

    if (chartVisual) {
      exportImage(chartVisual).then((dataURI) => saveAs(dataURI, widget.name + '.png'));
    }
  };

  const onPDFExportClick = () => {
    const chartVisual = exportVisual(chart.current, {
      width: PAGE_RECT.size.width,
    });

    if (chartVisual) {
      exportPDF(chartVisual, {
        landscape: true,
      }).then((dataURI) => saveAs(dataURI, widget.name + '.pdf'));
    }
  };

  const onSVGExportClick = () => {
    const chartVisual = exportVisual(chart.current);

    if (chartVisual) {
      exportSVG(chartVisual).then((dataURI) => saveAs(dataURI, widget.name + '.svg'));
    }
  };

  const excelExport = () => {
    if (exportRef.current !== null) {
      let exportColumns = [];
      grid.current.columns.forEach((column) => {
        if (column.children && column.children.length > 0) {
          column.children.forEach((child) => {
            exportColumns.push({
              field: child.field,
              title: `${child.title} ${column.title}`,
            });
          });
        } else {
          exportColumns.push({
            field: column.field,
            title: column.title,
          });
        }
      });
      exportRef.current.save(orderBy(gridData, sort), exportColumns);
    }
  };

  const createCFCell = (columnName, node, measure, dataItem, field) => {
    if (field !== undefined) {
      return (props) => <GridCell {...props} chartClick={chartClick} node={node} measure={measure} dataItem={dataItem}
                                  field={field} />;
    }
  };

  const clickOutsideHandler = () => {
    setPopUpVisible(false);
  };

  const serClickTest = (e) => {
    setPosition({ left: e.nativeEvent.x.location, top: e.nativeEvent.y.location });
    seriesClick(e);
    setPopUpVisible(true);
  };

  const chartClick = (e, dataItem, node, measure, field) => {
    setPosition({ left: e.pageX, top: e.pageY });
    gridCellClick(dataItem, field, measure.id, node.id);
    setPopUpVisible(true);
  };

  useClickOutside(popupWrapperRef, clickOutsideHandler, 'series-click-button');

  const ChartMemo = useMemo(
    () => (
      <>
        {!loading && (
          <Chart onSeriesClick={serClickTest} seriesColors={BrandingStore.series_colors} ref={chart}
                 style={{ position: 'relative', height: '100%' }}>
            {series[0] && series[0].tooltip.visible && <ChartTooltip render={series[0].tooltip.callbackTemplate} />}
            {series[0] && <ChartLegend position={setup.legend} visible={series[0].legend.visible} />}

            {categories && (
              <ChartCategoryAxis>
                {valueAxis.length === 1 &&
                  <ChartCategoryAxisItem name={'series-axes'} visible={false} categories={categories} />}

                <ChartCategoryAxisItem
                  majorGridLines={{ visible: false }}
                  name={'category'}
                  visible={true}
                  axisCrossingValue={valueAxis.length === 1 ? -100000000000000 : [-100000000000000, 100000000000000]}
                  categories={categories}
                  labels={{ rotation: 'auto' }}
                />
              </ChartCategoryAxis>
            )}
            <ChartValueAxis>
              {valueAxis.map((item, idx) => (
                <ChartValueAxisItem
                  key={idx}
                  name={item.name}
                  axisCrossingValue={valueAxis.length > 1 ? -100000000000 : [0, -100000000000000]}
                  labels={item.labels}
                  title={item.title}
                />
              ))}
            </ChartValueAxis>

            <ChartSeries>
              {series.map((item, idx) => {
                return item.stack ? (
                  <ChartSeriesItem
                    key={idx}
                    type={item.type}
                    field={'amount'}
                    stack={{ group: item.stack }}
                    data={item.data}
                    color={item.color}
                    name={item.name}
                    axis={item.axis}>
                    <ChartTooltip render={item.tooltip.callbackTemplate} />
                    {item.labels.visible ? (
                      <ChartSeriesLabels visible={item.labels.visible} position={item.labels.position}
                                         content={item.labels.callbackTemplate} />
                    ) : null}
                  </ChartSeriesItem>
                ) : (
                  <ChartSeriesItem key={idx} type={item.type} field={'amount'} data={item.data} color={item.color}
                                   name={item.name} axis={item.axis}>
                    <ChartSeriesItemTooltip render={item.tooltip.callbackTemplate} />
                    {item.labels.visible ? (
                      <ChartSeriesLabels visible={item.labels.visible} position={item.labels.position}
                                         content={item.labels.callbackTemplate} />
                    ) : null}
                  </ChartSeriesItem>
                );
              })}
            </ChartSeries>
          </Chart>
        )}
      </>
    ),
    [series, chartData, loading, expand],
  );

  useEffect(() => {
    let isMobile = window.innerWidth;
    if (isMobile < 960) {
      setIsMobile(true);
    }
  }, []);

  const handleSortChange = (e: GridSortChangeEvent) => {
    setSort(e.sort);
  };

  // @ts-ignore
  const GridMemo = useMemo(
    () => (
      <div className={'grid-widget-table chart-grid '}>
        {!loading && (
          <ExcelExport data={orderBy(gridData, sort)} ref={exportRef}>
            {
              // @ts-ignore
              gridOptions.columns && gridOptions.columns.map((item, key) => {
                if (item.field === 'periodName')
                  return <ExcelExportColumn field={item.field} key={key} title={item.title} />;
                if (item.columns) {
                  return <ExcelExportColumnGroup title={item.title} key={key}>
                    {
                      item.columns.map((child) => <ExcelExportColumn field={child.field} title={child.title} key={key} />)
                    }
                  </ExcelExportColumnGroup>;
                }
                return <ExcelExportColumn field={item.field} key={key} title={item.title} />;
              })
            }

            <Grid data={orderBy(gridData, sort)} sortable={true} sort={sort} onSortChange={handleSortChange} ref={grid}>
              {
                // @ts-ignore
                gridOptions.columns &&
                // @ts-ignore
                gridOptions.columns.map((item, idx) => {
                  return item.field === 'periodName' ? (
                    <GridColumn field={item.field} title={item.title} width={250} key={idx} locked={!isMobile} />
                  ) : item.columns ? (
                    <GridColumn
                      field={item.field}
                      title={item.title}
                      width={250}
                      key={idx}
                      cell={createCFCell(item.name, item.node, item.measure, gridData, item.field)}>
                      {
                        // @ts-ignore
                        item.columns &&
                        item.columns.map((nestedItem, i) => {
                          return (
                            <GridColumn
                              className='align-right'
                              field={nestedItem.field}
                              title={nestedItem.title}
                              width={250}
                              key={i}
                              format={`{0:${nestedItem.measure.isPercent ? 'p' : 'n'}${nestedItem.measure.scale}}`}
                            />
                          );
                        })
                      }
                    </GridColumn>
                  ) : (
                    <GridColumn field={item.field} title={item.title} width={250} key={idx}
                                format={`{0:${item.measure.isPercent ? 'p' : 'n'}${item.measure.scale}}`} />
                  );
                })
              }
            </Grid>
          </ExcelExport>
        )}
      </div>
    ),
    [series, chartData, loading, gridData, gridOptions],
  );

  return (
    <>
      {loading ? (
        <OutletLoader />
      ) : (
        <>
          <WidgetTitle
            name={widget.name}
            type={widget.type}
            currencySelection={(widget.pinned === null || widget.setup.currencySelection) ? currencies : false}
            handleCurrencyChange={handleCurrencyChange}
            selectedCurrency={selectedCurrency}
            onExpand={onExpand}
            onCollapse={onCollapse}
            expand={expand}
            setGridView={setGridView}
            gridView={gridView}
            onImageExportClick={onImageExportClick}
            onPDFExportClick={onPDFExportClick}
            onSVGExportClick={onSVGExportClick}
            excelExport={excelExport}
            notes={chartNotes}
            id={id}
            customisation={customisation}
            widgets={widgets}
            handleWidgetChange={handleWidgetChange}
            handleWidgetMove={handleWidgetMove}
            changeNewIndex={changeNewIndex}
            changeOldIndex={changeOldIndex}
            widget={widget}
            selectedObject={selectedObject}
            homePage={homePage}
            investorId={investorObjectId}
            handleUnpin={handleUnpin}
            handlePinWidget={handlePinWidget}
            handleUnpinWidget={handleUnpinWidget}
            unpinId={unpinId}
          />
          <div id={'bread-crumbs'} className={`bread-crumbs-absolute`}>
            {breadCrumbs.length > 1 &&
              breadCrumbs.map((x) => (
                <button className={`bread-crumb`} onClick={() => drillToLevel(x, widgetData)} key={x.level}>
                  {x.level === 0 ?
                    <Icon name='fa-solid fa-backward-fast' className={'pointer'} /> : ` / ${x.name}   `}
                </button>
              ))}
          </div>
          <div className={`chart-wrapper ${breadCrumbs.length > 1 ? 'breadcrumbs-on' : ''}`}>{!gridView ? ChartMemo : GridMemo}</div>
          <div ref={popupWrapperRef}>
            <Popup offset={position} show={popupVisible} popupClass={'popup-content'} className={'popup-wrapper'}
                   animate={false}>
              <div className={'pointer close-popup-button'} onClick={() => setPopUpVisible(false)}>
                <Icon name={'fa-solid fa-circle-xmark'} />
              </div>

              {cashFlow && (
                <>
                  <div>{cashFlow.valueInfo.name}</div>
                  <div> {cashFlow.period.name}</div>
                  <div>
                    {cashFlow.valueInfo.measure.name}:{' '}
                    {formatNumber(
                      cashFlow.value.amount ? cashFlow.value.amount : cashFlow.valueInfo.amount,
                      cashFlow.valueInfo.measure.isPercent ? `p${cashFlow.valueInfo.scale}` : `n${cashFlow.valueInfo.scale}`,
                    )}
                  </div>

                  <div className={'break-line'} />
                  {cashFlow.viewCashflows && (
                    <div
                      className={'series-click-button pointer'}
                      id={'series-click-button'}
                      onClick={() =>
                        dispatch({
                          type: SWITCH_CASH_FLOW_MODAL,
                          payload: {
                            dataItem: {
                              name: cashFlow.valueInfo.name,
                              currency: cashFlow.currency,
                            },
                            cashFlowInfo: cashFlow,
                            investorObjectId: investorObjectId,
                            columnName: cashFlow.valueInfo.measure.name,
                            value: formatNumber(
                              cashFlow.value.amount ? cashFlow.value.amount : cashFlow.valueInfo.amount,
                              cashFlow.valueInfo.measure.isPercent ? `p${cashFlow.valueInfo.scale}` : `n${cashFlow.valueInfo.scale}`,
                            ),
                            boardId: boardId,
                            selectedCurrency: selectedCurrency,
                          },
                        })
                      }>
                      View cash flows
                      <Icon name={' fa-regular fa-eye'} />
                    </div>
                  )}
                  {drillDownData &&
                    drillDownData.map((drillDownNode, idx) => (
                      <div className='series-click-button pointer' id={'series-click-button'}
                           onClick={() => drillDown(drillDownNode.id, widgetData)} key={idx}>
                        {drillDownNode.name || t('CHART_WIDGET.DRILL_DOWN')}
                        <Icon name={'fa-solid fa-split'} />
                      </div>
                    ))}
                </>
              )}
            </Popup>
          </div>
        </>
      )}
    </>
  );
};

export default ChartWidget;
